/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
/**
 * Internal Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react';
import { format } from 'date-fns';
import { logReportsClick } from '../../../utils/analyticsHelpers';
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import { setIssueFilter } from '../../../redux/actions';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';
import { ApiMetrics, Filter } from '../../../config/interfaces';

interface MetricsData {
  totalRecordsCount: number;
  metrics: { label: string; value: number; color: string }[];
}

interface TaskMetricChartProps {
  issueMetrics: ApiMetrics;
  patientSummaryReportFilter: Filter;
}

const TaskMetricChart: FC<TaskMetricChartProps> = ({
  issueMetrics,
  patientSummaryReportFilter
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: issueMetrics.totalRecordsCount,
      metrics: [
        {
          label: 'Opened',
          value: issueMetrics.openedRecordsCount,
          color: '#df5828'
        },
        {
          label: 'Completed',
          value: issueMetrics.completedRecordsCount,
          color: '#0077b5'
        }
      ]
    };
  }, [issueMetrics]);

  const openIssuesTable = () => {
    dispatch(
      setIssueFilter({
        ...patientSummaryReportFilter,
        startDate:
          patientSummaryReportFilter.startDate ??
          format(new Date(), 'yyyy-MM-dd'),
        category: '',
        subCategory: '',
        createdBy: '',
        patient: ''
      })
    );
    if (view)
      logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'issue'));
    setTimeout(() => {
      navigateTo('/patients/issues', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer onClick={openIssuesTable}>
      <HeaderContainer style={{ paddingLeft: '4.5em' }}>
        <NeuIcon size="1x" color="blue" className="ms-1">
          flag
        </NeuIcon>
        <TitleContainer style={{ marginLeft: '0.5rem' }}>
          {issueMetrics.totalRecordsCount} Issues
        </TitleContainer>
      </HeaderContainer>
      <TransitionalBarChart metricsData={metricsData} />
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    issueMetrics: state.TaskReducer.issueMetrics,
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter
  };
};
export default connect(mapReduxStateToProps)(TaskMetricChart);
